import api from './api';

export interface IntervisieGroup {
  id: string;
  group_number: number;
  name: string;
  description?: string;
  max_members: number;
  planning?: string;
  postcode?: string;
  straatnaam?: string;
  huisnummer?: string;
  toevoeging?: string;
  plaats?: string;
  provincie?: string;
  doelgroep?: string;
  status: 'beschikbaar' | 'bijna_vol' | 'vol';
  is_online: boolean;
  is_private: boolean;
  contact_first_name: string;
  contact_last_name: string;
  member_count: number;
  created_at: string;
}

export interface IntervisieGroupDetail extends IntervisieGroup {
  members: IntervisieGroupMember[];
  sessions: IntervisieSession[];
  user_membership: { role: string; status: string } | null;
  is_contact_person: boolean;
  /** Belagom heeft de Microsoft Teams-koppeling ingericht (migratie 152) */
  teams_beschikbaar?: boolean;
}

export interface IntervisieGroupMember {
  id: string;
  user_id: string;
  role: string;
  joined_at: string;
  first_name: string;
  last_name: string;
  profile_photo_path?: string;
}

export interface IntervisieSession {
  id: string;
  date: string;
  methodology: string;
  notes?: string;
  created_at: string;
  my_role?: string;
  my_attendance?: boolean;
  duur_minuten?: number;
  start_at?: string | null;
  is_teams?: boolean;
  /** de Teams-link zelf komt alleen via joinTeamsSession (de Grendel) */
  kan_deelnemen?: boolean;
  /** eigen beoordeling door de Compliance Monitor (migratie 161) */
  mijn_aanwezigheid?: { status: ComplianceStatus; pct: number; reden: AfwijsReden | null } | null;
  teams_sync_status?: 'gepland' | 'gesynchroniseerd' | 'fout' | null;
  teams_sync_fout?: string | null;
  teams_gesynchroniseerd_op?: string | null;
  aanwezig?: number;
  aanwezig_teams?: number;
  /** alleen voor de contactpersoon */
  attendees?: Array<{
    user_id: string;
    attended: boolean;
    bron: 'contactpersoon' | 'teams';
    teams_seconden: number | null;
    compliance_status: ComplianceStatus | null;
    aanwezigheid_pct: string | number | null;
    afwijs_reden: AfwijsReden | null;
    koppeling: 'email' | 'contactpersoon' | null;
  }>;
  /** alleen voor de contactpersoon: Teams-deelnemers die niet op e-mail herkend werden */
  teams_onbekende_deelnemers?: TeamsOnbekend[];
}

export type ComplianceStatus = 'ATTENDANCE_VERIFIED' | 'REJECTED';
export type AfwijsReden = 'ONDER_DREMPEL' | 'NIET_VIA_COCKPIT';
export interface TeamsOnbekend {
  sleutel: string;
  naam: string;
  minuten: number;
}

export interface TeamsSyncResultaat {
  gekoppeld: number;
  geverifieerd: number;
  afgewezen: number;
  onbekend: TeamsOnbekend[];
  rapporten: number;
}

export const METHODIEKEN = [
  'Roddelmethode',
  'STAR-methode',
  'Socratische methode',
  'Incidentmethode',
  'Balintmethode',
  'Helpende hand',
  'Andere methodiek',
];

/** Gangbare intervisieduur in minuten; standaard 120 (2 uur). */
export const DUUR_OPTIES = [60, 90, 120, 150, 180, 240];

export const intervisieApi = {
  getOptions: async (): Promise<{ planning_options: string[]; provincies: string[]; doelgroep_options: { value: string; label: string }[] }> => {
    const res = await api.get('/intervisie/options');
    return res.data.data;
  },

  getGroups: async (params?: {
    locatie?: string;
    provincie?: string;
    status?: string[];
    group_number?: string;
    doelgroep?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: IntervisieGroup[]; total: number }> => {
    const res = await api.get('/intervisie/groups', { params });
    return { data: res.data.data, total: res.data.total };
  },

  getGroup: async (id: string): Promise<IntervisieGroupDetail> => {
    const res = await api.get(`/intervisie/groups/${id}`);
    return res.data.data;
  },

  createGroup: async (data: {
    name: string;
    max_members: number;
    planning: string;
    postcode?: string;
    straatnaam?: string;
    huisnummer?: string;
    toevoeging?: string;
    plaats?: string;
    provincie?: string;
    description?: string;
    is_online?: boolean;
    doelgroep?: string;
    akkoord_vermelding: boolean;
  }): Promise<IntervisieGroup> => {
    const res = await api.post('/intervisie/groups', data);
    return res.data.data;
  },

  joinGroup: async (id: string) => {
    const res = await api.post(`/intervisie/groups/${id}/join`);
    return res.data;
  },

  registerSession: async (data: {
    group_id: string;
    date: string;
    methodology: string;
    facilitator_id?: string;
    inbrenger_id?: string;
    notes?: string;
    duur_minuten?: number;
    attendees: Array<{ user_id: string; role: string; attended: boolean }>;
  }) => {
    const res = await api.post('/intervisie/sessions', data);
    return res.data.data;
  },

  updateSessionDuration: async (sessionId: string, duur_minuten: number) => {
    const res = await api.patch(`/intervisie/sessions/${sessionId}`, { duur_minuten });
    return res.data;
  },

  planTeamsSession: async (groupId: string, data: { start_at: string; duur_minuten: number; methodology?: string }) => {
    const res = await api.post(`/intervisie/groups/${groupId}/teams-sessies`, data);
    return res.data.data;
  },

  /** De Grendel: logt de klik en geeft de Teams-link terug. */
  joinTeamsSession: async (sessionId: string): Promise<{ join_url: string }> => {
    const res = await api.post(`/intervisie/sessions/${sessionId}/deelnemen`);
    return res.data.data;
  },

  linkTeamsParticipant: async (sessionId: string, user_id: string, sleutel: string): Promise<TeamsSyncResultaat> => {
    const res = await api.post(`/intervisie/sessions/${sessionId}/teams-koppeling`, { user_id, sleutel });
    return res.data.data;
  },

  syncTeamsAttendance: async (sessionId: string): Promise<TeamsSyncResultaat> => {
    const res = await api.post(`/intervisie/sessions/${sessionId}/teams-sync`);
    return res.data.data;
  },

  getMyGroups: async (): Promise<IntervisieGroupDetail[]> => {
    const res = await api.get('/intervisie/my-groups');
    return res.data.data;
  },
};
